import React, { useEffect, useState } from 'react';
import { getCatalogAnalytics } from '../services/AnalyticsService';
import { useAuth } from '../context/AuthContext';
import './MarketingPanel.css';
import API_BASE_URL from '../config/api.js';

const API_BASE = API_BASE_URL;

export default function MarketingPanel({ user }) {
  const { artistData } = useAuth();
  const [marketingData, setMarketingData] = useState(null);
  const [buzz, setBuzz] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMarketingData = async () => {
      try {
        setLoading(true);
        
        // Get catalog analytics with marketing data
        const artistId = artistData?.id || 'rdv01';
        const analyticsData = await getCatalogAnalytics(artistId);
        
        if (analyticsData && analyticsData.marketing) {
          setMarketingData(analyticsData.marketing);
        }
        
        // Also fetch industry buzz
        fetch(`${API_BASE}/industry_buzz.txt`)
          .then(async (res) => {
            if (!res.ok) return '';
            const contentType = res.headers.get('content-type');
            if (!contentType || !contentType.includes('text')) return '';
            return await res.text();
          })
          .then((text) => setBuzz(text.trim()))
          .catch(() => {
            // ignore fetch errors
          });
          
      } catch (err) {
        console.error('Error fetching marketing data:', err);
        setError('Failed to load marketing data');
      } finally {
        setLoading(false);
      }
    };

    fetchMarketingData();
  }, [artistData]);

  if (loading) {
    return (
      <div className="professional-container">
        <div className="professional-loading">
          Loading marketing dashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="professional-container">
        <div className="professional-error">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="professional-container">
      {/* Header */}
      <div className="professional-header">
        <h1>📈 Social Marketing Dashboard</h1>
        <p>Campaign management and brand opportunities for {user?.username || artistData?.name || 'Artist'}</p>
      </div>

      {/* Marketing Metrics Overview - Horizontal Layout */}
      <div className="professional-grid-4">
        <div className="professional-metric-card">
          <div className="professional-metric-value">{marketingData?.campaignReady?.length || 0}</div>
          <div className="professional-metric-label">Campaign Ready</div>
        </div>
        <div className="professional-metric-card">
          <div className="professional-metric-value">{marketingData?.allTracks?.length || 0}</div>
          <div className="professional-metric-label">Total Tracks</div>
        </div>
        <div className="professional-metric-card">
          <div className="professional-metric-value">
            {marketingData?.brandOpportunities ? Object.keys(marketingData.brandOpportunities).length : 0}
          </div>
          <div className="professional-metric-label">Brand Categories</div>
        </div>
        <div className="professional-metric-card">
          <div className="professional-metric-value">
            {marketingData?.optimalPostingTimes ? Object.keys(marketingData.optimalPostingTimes).length : 0}
          </div>
          <div className="professional-metric-label">Optimal Time Slots</div>
        </div>
      </div>

      {/* Campaign Ready Tracks - Horizontal Card Layout */}
      {marketingData?.campaignReady && marketingData.campaignReady.length > 0 && (
        <div className="professional-card">
          <div className="professional-card-header">
            <h3 className="professional-card-title">🎯 Campaign Ready Tracks</h3>
          </div>
          <div className="professional-card-content">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {marketingData.campaignReady.map((track, index) => (
                <div key={track.track_id || index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  gap: '2rem'
                }}>
                  {/* Track Info - Left Side */}
                  <div style={{ flex: '2', minWidth: '200px' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--accent-color)', fontSize: '1.1rem' }}>
                      {track.title}
                    </h4>
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                      <span>{track.genre}</span>
                      <span>•</span>
                      <span>{track.bpm} BPM</span>
                      <span>•</span>
                      <span>{track.duration}</span>
                    </div>
                    <p style={{ margin: '0.5rem 0 0 0', fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      "{track.vibe}"
                    </p>
                  </div>

                  {/* Viral Score - Center */}
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '0.75rem 1rem',
                    background: 'rgba(0, 255, 136, 0.1)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 255, 136, 0.2)'
                  }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: '700', color: 'var(--accent-color)' }}>
                      {track.viralScore}%
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      Viral Score
                    </div>
                  </div>

                  {/* Actions - Right Side */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '150px' }}>
                    {track.spotify_url && (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <a 
                          href={track.spotify_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="professional-button"
                          style={{ fontSize: '0.8rem', padding: '0.5rem 1rem', textDecoration: 'none' }}
                        >
                          🎵 Spotify
                        </a>
                        <button 
                          onClick={() => navigator.clipboard.writeText(track.spotify_url)}
                          className="professional-button professional-button-secondary"
                          style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
                        >
                          📋 Copy
                        </button>
                      </div>
                    )}
                    
                    {/* Platform Tags */}
                    {track.platforms && track.platforms.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {track.platforms.map(platform => (
                          <span key={platform} style={{
                            fontSize: '0.7rem',
                            padding: '0.25rem 0.5rem',
                            background: 'rgba(0, 255, 136, 0.2)',
                            borderRadius: '4px',
                            color: 'var(--accent-color)'
                          }}>
                            {platform}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid - 2 Column Layout */}
      <div className="professional-grid-2">
        {/* All Tracks Overview */}
        {marketingData?.allTracks && marketingData.allTracks.length > 0 && (
          <div className="professional-card">
            <div className="professional-card-header">
              <h3 className="professional-card-title">📚 Full Catalog Status</h3>
            </div>
            <div className="professional-card-content">
              <ul className="professional-list">
                {marketingData.allTracks.slice(0, 6).map((track, index) => (
                  <li key={track.track_id || index} className="professional-list-item">
                    <div className="professional-list-item-content">
                      <div className="professional-list-item-title">{track.title}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span className={`professional-list-item-value ${track.marketing_ready ? 'positive' : 'warning'}`}>
                        {track.marketing_ready ? '✅ Ready' : '⏳ Pending'}
                      </span>
                      {track.spotify_url && (
                        <a 
                          href={track.spotify_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', fontSize: '1.2rem' }}
                        >
                          🎵
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {marketingData.allTracks.length > 6 && (
                <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  +{marketingData.allTracks.length - 6} more tracks
                </div>
              )}
            </div>
          </div>
        )}

        {/* Brand Opportunities */}
        {marketingData?.brandOpportunities && (
          <div className="professional-card">
            <div className="professional-card-header">
              <h3 className="professional-card-title">🏢 Brand Partnership Opportunities</h3>
            </div>
            <div className="professional-card-content">
              <div className="brand-opportunities-row" style={{ display: 'flex', gap: 'var(--grid-gap)', flexWrap: 'wrap' }}>
                {Object.entries(marketingData.brandOpportunities).map(([category, count]) => (
                  <div key={category} className="professional-metric-card" style={{ flex: '1 1 200px', minWidth: '150px' }}>
                    <div className="professional-metric-value">{count}</div>
                    <div className="professional-metric-label">{category}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Section - 2 Column Layout */}
      <div className="professional-grid-2">
        {/* Optimal Posting Times */}
        {marketingData?.optimalPostingTimes && (
          <div className="professional-card">
            <div className="professional-card-header">
              <h3 className="professional-card-title">⏰ Optimal Posting Times</h3>
            </div>
            <div className="professional-card-content">
              <ul className="professional-list">
                {Object.entries(marketingData.optimalPostingTimes).map(([time, score]) => (
                  <li key={time} className="professional-list-item">
                    <div className="professional-list-item-content">
                      <div className="professional-list-item-title">{time}</div>
                    </div>
                    <div className="professional-list-item-value">
                      Score: {score}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Industry Buzz */}
        {buzz && (
          <div className="professional-card">
            <div className="professional-card-header">
              <h3 className="professional-card-title">📰 Industry Buzz</h3>
            </div>
            <div className="professional-card-content">
              <pre style={{ 
                whiteSpace: 'pre-wrap', 
                fontSize: '0.8rem', 
                lineHeight: '1.4',
                color: 'var(--text-secondary)',
                margin: 0,
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {buzz}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
